import { memo } from 'react';
import { FlexBox, FormAsyncSelectAutocompleteField, IconButton } from '@dfl/mui-react-common';
import { Box, Checkbox, Typography } from '@mui/material';
import { isOptionEqualToValue } from 'utils/comparing';
import { IStockReductionCause } from 'modules/inventory/settings/stock-reduction-cause/interfaces';
import { STOCK_REDUCTION_CAUSES_LIST_KEY } from 'modules/inventory/settings/stock-reduction-cause/constants';
import { StockReductionCauseService } from 'modules/inventory/settings/stock-reduction-cause/services';
import { Image, PersonAddAlt1 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type StockReductionCauseSelectProps = {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  multiple?: boolean;
};

const renderLabel = (option: IStockReductionCause) => option?.name || '';

const StockReductionCauseSelect = ({
  name,
  required,
  multiple,
  label,
  helperText,
  ...props
}: StockReductionCauseSelectProps) => {
  const { t } = useTranslation('stock');

  const renderOption = (props: any, option: IStockReductionCause, { selected }: any) => {
    return (
      <li {...props} key={option?._id as string}>
        <Checkbox checked={selected} />
        <Box>
          <FlexBox alignItems={'center'} gap={0.5}>
            <Typography>{option?.name}</Typography>
            {option?.requiresResponsible && (
              <IconButton sx={{ width: 20, height: 20 }} tooltip={t('fields.requiresResponsible')} size='small' color='warning'>
                <PersonAddAlt1 sx={{ width: 18, height: 18 }} fontSize='small' />
              </IconButton>
            )}
            {option?.requiresEvidence && (
              <IconButton sx={{ width: 20, height: 20 }} tooltip={t('fields.requiresEvidence')} size='small' color='warning'>
                <Image fontSize='small' sx={{ width: 18, height: 18 }} />
              </IconButton>
            )}
          </FlexBox>
          <Typography>{option?.description}</Typography>
        </Box>
      </li>
    );
  };
  return (
    <FormAsyncSelectAutocompleteField
      {...props}
      multiple={multiple}
      required={required}
      label={label}
      name={name}
      disableCloseOnSelect={multiple}
      fetchFunc={StockReductionCauseService.search}
      queryKey={STOCK_REDUCTION_CAUSES_LIST_KEY}
      autoHighlight
      isOptionEqualToValue={isOptionEqualToValue}
      loadValue
      fetchValueFunc={multiple ? StockReductionCauseService.search : StockReductionCauseService.getOne}
      id='select-stock-reduction-cause'
      getOptionLabel={renderLabel}
      renderOption={renderOption}
      helperText={helperText}
    />
  );
};

export default memo(StockReductionCauseSelect);
